// Auto-focus helpers for forms and modals

"use strict";

import { nextTick, type Ref } from "vue";

/**
 * Focuses an element inside a root node, or the root itself as fallback.
 * @param rootEl Container element to search within
 * @param className CSS class of the target element; defaults to `auto-focus`
 */
export function autoFocusElement(rootEl: HTMLElement | null | undefined, className?: string): void {
    nextTick(() => {
        if (!rootEl) {
            return;
        }

        const elem = rootEl.querySelector("." + (className || "auto-focus")) as HTMLElement | null;

        if (elem) {
            elem.focus();
            if ("select" in elem && typeof elem.select === "function") {
                elem.select();
            }
        } else {
            rootEl.focus();
        }
    });
}

/**
 * Composable that returns a function to auto-focus within a template ref.
 * @param rootRef Reactive reference to the container element
 * @returns Function that focuses the matching child or the container
 */
export function useAutoFocus(rootRef: Ref<HTMLElement | null | undefined>) {
    /**
     * Focuses an element inside the bound container.
     * @param className CSS class of the target element; defaults to `auto-focus`
     */
    return (className?: string) => {
        autoFocusElement(rootRef.value, className);
    };
}
